import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCompany } from '../../hooks/useCompany';
import { profitBudgetAPI } from '../../lib/api';
import { toast } from 'sonner';
import {
  Plus,
  Calendar,
  DollarSign,
  Trash2,
  RefreshCw,
  ChevronDown,
  Check,
  X,
  Sparkles,
  TrendingUp,
  TrendingDown,
  Package,
  FileText,
  RotateCcw,
} from 'lucide-react';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatCurrency = (value) => {
  const num = parseFloat(value) || 0;
  return num < 0 
    ? `-$${Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
    : `$${num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

// Budget categories with their styling
const BUDGET_CATEGORIES = {
  revenue: {
    name: 'Revenue',
    icon: TrendingUp,
    headerBg: 'bg-green-50',
    headerText: 'text-green-700',
    rowBg: 'bg-green-50/50',
    totalBg: 'bg-green-100',
    totalText: 'text-green-800',
  },
  cogs: {
    name: 'Cost of Goods Sold',
    icon: DollarSign,
    headerBg: 'bg-yellow-50',
    headerText: 'text-yellow-700',
    rowBg: 'bg-yellow-50/50',
    totalBg: 'bg-yellow-100',
    totalText: 'text-yellow-800',
  },
  expenses: {
    name: 'Operating Expenses',
    icon: TrendingDown,
    headerBg: 'bg-red-50',
    headerText: 'text-red-700',
    rowBg: 'bg-red-50/50',
    totalBg: 'bg-red-100',
    totalText: 'text-red-800',
  },
};

// Editable Cell Component
function EditableCell({ value, onChange, className = '', disabled = false }) {
  const [localValue, setLocalValue] = useState(value);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleBlur = () => {
    setIsEditing(false);
    const numValue = parseFloat(localValue) || 0;
    if (numValue !== value) {
      onChange(numValue);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') e.target.blur();
    if (e.key === 'Escape') {
      setLocalValue(value);
      setIsEditing(false);
    }
  };

  if (disabled) {
    return (
      <div className={`px-2 py-1.5 text-right text-sm font-medium ${className}`}>
        {formatCurrency(value)}
      </div>
    );
  }

  return (
    <input
      type="number"
      value={isEditing ? localValue : ''}
      placeholder={formatCurrency(value)}
      onChange={(e) => setLocalValue(e.target.value)}
      onFocus={() => {
        setIsEditing(true);
        setLocalValue(value || '');
      }}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={`w-full px-2 py-1.5 text-right text-sm bg-transparent border-0 focus:outline-none focus:ring-2 focus:ring-primary/50 rounded placeholder:text-current ${className}`}
    />
  );
}

// Budget Row Component
function BudgetRow({ account, category, onUpdate, onDelete, onMonthlyUpdate, onDistribute }) {
  const categoryStyle = BUDGET_CATEGORIES[category];
  
  const handleAnnualChange = (newValue) => {
    onUpdate(account.id, { annual_budget: newValue });
  };

  const handleMonthlyChange = (month, newValue) => {
    onMonthlyUpdate(account.id, month, newValue);
  };

  const annualBudget = account.annual_budget || 0;

  return (
    <tr className={`border-b border-border/30 hover:bg-muted/20 group ${categoryStyle.rowBg}`}>
      <td className="py-2 px-3 min-w-[180px]">
        <div className="flex items-center justify-between">
          <span className="text-sm">{account.name}</span>
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
            <button
              onClick={() => onDistribute(account.id)}
              className="p-1 hover:bg-white/50 rounded text-muted-foreground hover:text-foreground"
              title="Distribute evenly"
            >
              <RotateCcw className="h-3 w-3" />
            </button>
            <button
              onClick={() => onDelete(account.id)}
              className="p-1 hover:bg-red-100 rounded text-muted-foreground hover:text-red-600"
              title="Delete"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        </div>
      </td>
      {/* Annual Budget Column */}
      <td className="py-1 px-0.5 min-w-[90px] bg-primary/5">
        <EditableCell
          value={annualBudget}
          onChange={handleAnnualChange}
          className="font-semibold"
        />
      </td>
      {/* Monthly Columns */}
      {MONTHS.map((_, idx) => {
        const month = idx + 1;
        const value = account.monthly_budgets?.[month] || account.monthly_budgets?.[String(month)] || 0;
        return (
          <td key={month} className="py-1 px-0.5 min-w-[70px]">
            <EditableCell
              value={value}
              onChange={(newVal) => handleMonthlyChange(month, newVal)}
            />
          </td>
        );
      })}
      <td className={`py-2 px-3 text-right font-semibold text-sm min-w-[85px] ${categoryStyle.totalText}`}>
        {formatCurrency(account.total_monthly || annualBudget || 0)}
      </td>
    </tr>
  );
}

// Category Total Row
function CategoryTotalRow({ label, annualTotal, monthlyTotals, total, style }) {
  return (
    <tr className={`${style.totalBg} border-b border-border`}>
      <td className={`py-2 px-3 font-semibold text-sm ${style.totalText}`}>{label}</td>
      <td className={`py-2 px-2 text-right font-semibold text-sm ${style.totalText} bg-primary/5`}>
        {formatCurrency(annualTotal || 0)}
      </td>
      {MONTHS.map((_, idx) => (
        <td key={idx} className={`py-2 px-2 text-right font-semibold text-sm ${style.totalText}`}>
          {formatCurrency(monthlyTotals[idx] || 0)}
        </td>
      ))}
      <td className={`py-2 px-3 text-right font-bold text-sm ${style.totalText}`}>
        {formatCurrency(total)}
      </td>
    </tr>
  );
}

// Calculated Row (Gross Profit, Net Profit)
function CalculatedRow({ label, annualValue, monthlyValues, total, isProfit }) {
  const textColor = total >= 0 ? 'text-green-700' : 'text-red-700';
  return (
    <tr className="bg-blue-50 border-b-2 border-blue-200">
      <td className={`py-3 px-3 font-bold text-sm ${textColor}`}>{label}</td>
      <td className={`py-3 px-2 text-right font-bold text-sm bg-blue-100/50 ${annualValue >= 0 ? 'text-green-700' : 'text-red-700'}`}>
        {formatCurrency(annualValue || 0)}
      </td>
      {MONTHS.map((_, idx) => (
        <td key={idx} className={`py-3 px-2 text-right font-bold text-sm ${monthlyValues[idx] >= 0 ? 'text-green-700' : 'text-red-700'}`}>
          {formatCurrency(monthlyValues[idx] || 0)}
        </td>
      ))}
      <td className={`py-3 px-3 text-right font-bold text-sm ${textColor}`}>
        {formatCurrency(total)}
      </td>
    </tr>
  );
}

// Section Header Row
function SectionHeader({ category, onAddAccount }) {
  const style = BUDGET_CATEGORIES[category];
  const Icon = style.icon;
  
  return (
    <tr className={`${style.headerBg} border-b border-border`}>
      <td colSpan={15} className="py-2 px-3">
        <div className="flex items-center justify-between">
          <div className={`flex items-center gap-2 ${style.headerText} font-semibold`}>
            <Icon className="h-4 w-4" />
            <span>{style.name}</span>
          </div>
          <button
            onClick={() => onAddAccount(category)}
            className={`flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-white/50 ${style.headerText}`}
          >
            <Plus className="h-3 w-3" />
            Add Line
          </button>
        </div>
      </td>
    </tr>
  );
}

// New Account Form Row
function NewAccountRow({ category, onSubmit, onCancel }) {
  const [name, setName] = useState('');
  const style = BUDGET_CATEGORIES[category];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Please enter an account name');
      return;
    }
    onSubmit({ name: name.trim(), category });
    setName('');
  };

  return (
    <tr className={`${style.rowBg} border-b border-border`}>
      <td className="py-2 px-3">
        <form onSubmit={handleSubmit} className="flex items-center gap-2">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={`New ${style.name.toLowerCase()} item...`}
            className="flex-1 px-2 py-1 text-sm border border-border rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
            autoFocus
          />
          <button type="submit" className="p-1 bg-primary text-white rounded hover:bg-primary/90">
            <Check className="h-3 w-3" />
          </button>
          <button type="button" onClick={onCancel} className="p-1 bg-gray-200 rounded hover:bg-gray-300">
            <X className="h-3 w-3" />
          </button>
        </form>
      </td>
      <td colSpan={14} className="py-2 px-3 text-xs text-muted-foreground italic">
        Press Enter to add, Escape to cancel
      </td>
    </tr>
  );
}

// Year Selector
function YearSelector({ years, selectedYearId, onSelect, onCreateNew }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newYear, setNewYear] = useState(new Date().getFullYear() + 1);
  const [newName, setNewName] = useState('');

  const selectedYear = years?.find(y => y.id === selectedYearId);

  const handleCreate = () => {
    onCreateNew({ year: newYear, name: newName || `FY ${newYear} P&L Budget` });
    setShowNewForm(false);
    setShowDropdown(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-lg hover:bg-muted"
      >
        <Calendar className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium">
          {selectedYear ? `${selectedYear.year} | USD` : 'Select Year'}
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
      </button>

      {showDropdown && (
        <div className="absolute top-full left-0 mt-1 w-72 bg-card border border-border rounded-lg shadow-lg z-20">
          <div className="max-h-60 overflow-y-auto">
            {years?.map(y => (
              <button
                key={y.id}
                onClick={() => { onSelect(y.id); setShowDropdown(false); }}
                className={`w-full px-4 py-2 text-left hover:bg-muted flex items-center justify-between ${
                  y.id === selectedYearId ? 'bg-primary/10' : ''
                }`}
              >
                <span>{y.name} ({y.year})</span>
                {y.id === selectedYearId && <Check className="h-4 w-4 text-primary" />}
              </button>
            ))}
          </div>
          <div className="p-2 border-t border-border">
            {showNewForm ? (
              <div className="space-y-2 p-2">
                <div className="flex gap-2">
                  <select
                    value={newYear}
                    onChange={(e) => setNewYear(parseInt(e.target.value))}
                    className="px-2 py-1 border border-border rounded text-sm"
                  >
                    {[2024, 2025, 2026, 2027, 2028].map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder={`FY ${newYear} P&L Budget`}
                    className="flex-1 px-2 py-1 border border-border rounded text-sm"
                  />
                </div>
                <div className="flex gap-2">
                  <button onClick={handleCreate} className="flex-1 px-3 py-1 bg-primary text-primary-foreground rounded text-sm">
                    Create
                  </button>
                  <button onClick={() => setShowNewForm(false)} className="px-3 py-1 border border-border rounded text-sm">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowNewForm(true)}
                className="w-full px-4 py-2 text-left text-sm text-primary hover:bg-primary/10 rounded flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Create New Budget Year
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Main Budget Planning Page
export default function BudgetPlanning() {
  const { selectedCompany } = useCompany();
  const queryClient = useQueryClient();
  const [selectedYearId, setSelectedYearId] = useState(null);
  const [addingCategory, setAddingCategory] = useState(null);

  // Fetch budget years
  const { data: years = [], isLoading: yearsLoading } = useQuery({
    queryKey: ['budget-years', selectedCompany?.id],
    queryFn: () => profitBudgetAPI.getYears(selectedCompany?.id).then(res => res.data),
    enabled: !!selectedCompany?.id,
  });

  // Auto-select first year
  useEffect(() => {
    if (years.length > 0 && !selectedYearId) {
      setSelectedYearId(years[0].id);
    }
  }, [years, selectedYearId]);

  // Fetch selected year data
  const { data: yearData, isLoading: yearLoading } = useQuery({
    queryKey: ['budget-year', selectedYearId],
    queryFn: () => profitBudgetAPI.getYear(selectedYearId).then(res => res.data),
    enabled: !!selectedYearId,
  });

  // Mutations
  const createYearMutation = useMutation({
    mutationFn: (data) => profitBudgetAPI.createYear(selectedCompany?.id, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['budget-years']);
      setSelectedYearId(response.data.id);
      toast.success('Budget year created');
    },
    onError: (error) => toast.error(error.response?.data?.detail || 'Failed to create'),
  });

  const createAccountMutation = useMutation({
    mutationFn: (data) => profitBudgetAPI.createAccount(selectedYearId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['budget-year', selectedYearId]);
      setAddingCategory(null);
      toast.success('Account added');
    },
    onError: (error) => toast.error(error.response?.data?.detail || 'Failed to add'),
  });

  const updateAccountMutation = useMutation({
    mutationFn: ({ accountId, data }) => profitBudgetAPI.updateAccount(accountId, data),
    onSuccess: () => queryClient.invalidateQueries(['budget-year', selectedYearId]),
  });

  const updateMonthlyMutation = useMutation({
    mutationFn: ({ accountId, month, amount }) => profitBudgetAPI.updateMonthly(accountId, month, amount, true),
    onSuccess: () => queryClient.invalidateQueries(['budget-year', selectedYearId]),
  });

  const distributeMutation = useMutation({
    mutationFn: (accountId) => profitBudgetAPI.distributeEvenly(accountId),
    onSuccess: () => {
      queryClient.invalidateQueries(['budget-year', selectedYearId]);
      toast.success('Distributed evenly');
    },
  });

  const deleteAccountMutation = useMutation({
    mutationFn: (accountId) => profitBudgetAPI.deleteAccount(accountId),
    onSuccess: () => {
      queryClient.invalidateQueries(['budget-year', selectedYearId]);
      toast.success('Account deleted');
    },
  });

  // Handlers
  const handleUpdate = useCallback((accountId, data) => {
    updateAccountMutation.mutate({ accountId, data });
  }, [updateAccountMutation]);

  const handleMonthlyUpdate = useCallback((accountId, month, amount) => {
    updateMonthlyMutation.mutate({ accountId, month, amount });
  }, [updateMonthlyMutation]);

  // Organize accounts by category
  const accounts = yearData?.accounts || [];
  const revenueAccounts = accounts.filter(a => a.category === 'revenue' || a.name?.includes('Loan') || a.name?.includes('Money') || a.name?.includes('Card') || a.name?.includes('Float') || a.name?.includes('FX') || (!a.category && !a.name?.includes('Cost') && !a.name?.includes('Expense')));
  const cogsAccounts = accounts.filter(a => a.category === 'cogs' || a.name?.toLowerCase().includes('cost'));
  const expenseAccounts = accounts.filter(a => a.category === 'expenses' || a.name?.toLowerCase().includes('expense'));

  // Calculate totals
  const calculateMonthlyTotals = (accts) => {
    return MONTHS.map((_, idx) => {
      const month = idx + 1;
      return accts.reduce((sum, a) => {
        const val = a.monthly_budgets?.[month] || a.monthly_budgets?.[String(month)] || 0;
        return sum + val;
      }, 0);
    });
  };

  const calculateTotal = (accts) => {
    return accts.reduce((sum, a) => sum + (a.total_monthly || a.annual_budget || 0), 0);
  };

  const calculateAnnualTotal = (accts) => {
    return accts.reduce((sum, a) => sum + (a.annual_budget || 0), 0);
  };

  const revenueMonthly = calculateMonthlyTotals(revenueAccounts);
  const cogsMonthly = calculateMonthlyTotals(cogsAccounts);
  const expenseMonthly = calculateMonthlyTotals(expenseAccounts);
  
  const revenueTotal = calculateTotal(revenueAccounts);
  const cogsTotal = calculateTotal(cogsAccounts);
  const expenseTotal = calculateTotal(expenseAccounts);

  const revenueAnnual = calculateAnnualTotal(revenueAccounts);
  const cogsAnnual = calculateAnnualTotal(cogsAccounts);
  const expenseAnnual = calculateAnnualTotal(expenseAccounts);

  const grossProfitMonthly = MONTHS.map((_, idx) => revenueMonthly[idx] - cogsMonthly[idx]);
  const grossProfitTotal = revenueTotal - cogsTotal;
  const grossProfitAnnual = revenueAnnual - cogsAnnual;
  
  const netProfitMonthly = MONTHS.map((_, idx) => grossProfitMonthly[idx] - expenseMonthly[idx]);
  const netProfitTotal = grossProfitTotal - expenseTotal;
  const netProfitAnnual = grossProfitAnnual - expenseAnnual;

  if (yearsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="budget-planning-page">
      {/* Header */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-muted rounded">
              <FileText className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-semibold">{selectedCompany?.name || 'Company'}</h1>
                <YearSelector
                  years={years}
                  selectedYearId={selectedYearId}
                  onSelect={setSelectedYearId}
                  onCreateNew={(data) => createYearMutation.mutate(data)}
                />
              </div>
              <p className="text-sm text-muted-foreground">
                {yearData ? `${accounts.length} lines` : 'Select a budget year'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Gross Profit</p>
              <p className={`text-lg font-bold ${grossProfitTotal >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(grossProfitTotal)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Net Profit</p>
              <p className={`text-lg font-bold ${netProfitTotal >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(netProfitTotal)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* P&L Budget Table */}
      {selectedYearId ? (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50 border-b border-border">
                  <th className="text-left py-3 px-3 font-medium min-w-[180px]">Account</th>
                  <th className="text-right py-3 px-2 font-medium min-w-[90px] bg-primary/10">Annual</th>
                  {MONTHS.map(m => (
                    <th key={m} className="text-right py-3 px-2 font-medium min-w-[70px]">{m}</th>
                  ))}
                  <th className="text-right py-3 px-3 font-medium min-w-[85px]">Total</th>
                </tr>
              </thead>
              <tbody>
                {/* Revenue Section */}
                <SectionHeader category="revenue" onAddAccount={setAddingCategory} />
                {revenueAccounts.map(account => (
                  <BudgetRow
                    key={account.id}
                    account={account}
                    category="revenue"
                    onUpdate={handleUpdate}
                    onDelete={(id) => deleteAccountMutation.mutate(id)}
                    onMonthlyUpdate={handleMonthlyUpdate}
                    onDistribute={(id) => distributeMutation.mutate(id)}
                  />
                ))}
                {addingCategory === 'revenue' && (
                  <NewAccountRow
                    category="revenue"
                    onSubmit={(data) => createAccountMutation.mutate(data)}
                    onCancel={() => setAddingCategory(null)}
                  />
                )}
                <CategoryTotalRow
                  label="Total Revenue"
                  annualTotal={revenueAnnual}
                  monthlyTotals={revenueMonthly}
                  total={revenueTotal}
                  style={BUDGET_CATEGORIES.revenue}
                />

                {/* Cost of Goods Sold Section */}
                <SectionHeader category="cogs" onAddAccount={setAddingCategory} />
                {cogsAccounts.map(account => (
                  <BudgetRow
                    key={account.id}
                    account={account}
                    category="cogs"
                    onUpdate={handleUpdate}
                    onDelete={(id) => deleteAccountMutation.mutate(id)}
                    onMonthlyUpdate={handleMonthlyUpdate}
                    onDistribute={(id) => distributeMutation.mutate(id)}
                  />
                ))}
                {addingCategory === 'cogs' && (
                  <NewAccountRow
                    category="cogs"
                    onSubmit={(data) => createAccountMutation.mutate(data)}
                    onCancel={() => setAddingCategory(null)}
                  />
                )}
                {cogsAccounts.length > 0 && (
                  <CategoryTotalRow
                    label="Total COGS"
                    annualTotal={cogsAnnual}
                    monthlyTotals={cogsMonthly}
                    total={cogsTotal}
                    style={BUDGET_CATEGORIES.cogs}
                  />
                )}

                {/* Gross Profit */}
                <CalculatedRow
                  label="Gross Profit"
                  annualValue={grossProfitAnnual}
                  monthlyValues={grossProfitMonthly}
                  total={grossProfitTotal}
                  isProfit
                />

                {/* Operating Expenses Section */}
                <SectionHeader category="expenses" onAddAccount={setAddingCategory} />
                {expenseAccounts.map(account => (
                  <BudgetRow
                    key={account.id}
                    account={account}
                    category="expenses"
                    onUpdate={handleUpdate}
                    onDelete={(id) => deleteAccountMutation.mutate(id)}
                    onMonthlyUpdate={handleMonthlyUpdate}
                    onDistribute={(id) => distributeMutation.mutate(id)}
                  />
                ))}
                {addingCategory === 'expenses' && (
                  <NewAccountRow
                    category="expenses"
                    onSubmit={(data) => createAccountMutation.mutate(data)}
                    onCancel={() => setAddingCategory(null)}
                  />
                )}
                {expenseAccounts.length > 0 && (
                  <CategoryTotalRow
                    label="Total Expenses"
                    annualTotal={expenseAnnual}
                    monthlyTotals={expenseMonthly}
                    total={expenseTotal}
                    style={BUDGET_CATEGORIES.expenses}
                  />
                )}

                {/* Net Profit */}
                <CalculatedRow
                  label="Net Profit"
                  annualValue={netProfitAnnual}
                  monthlyValues={netProfitMonthly}
                  total={netProfitTotal}
                  isProfit
                />
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Budget Year Selected</h3>
          <p className="text-muted-foreground mb-4">Create a new budget year to start planning</p>
          <button
            onClick={() => createYearMutation.mutate({ 
              year: new Date().getFullYear() + 1, 
              name: `FY ${new Date().getFullYear() + 1} P&L Budget` 
            })}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            Create Budget Year
          </button>
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-100 border border-green-200 rounded"></div>
          <span>Revenue</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-yellow-100 border border-yellow-200 rounded"></div>
          <span>Cost of Goods Sold</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-blue-100 border border-blue-200 rounded"></div>
          <span>Profit (calculated)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-red-100 border border-red-200 rounded"></div>
          <span>Operating Expenses</span>
        </div>
      </div>
    </div>
  );
}
